// Force Recompile
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  AreaChart, Area
} from 'recharts';
import {
  Users, BookOpen, Target, Activity, Calendar, FileText, Settings,
  LogOut, Plus, Search, Bell, Menu, X, ChevronRight, ChevronDown, Award,
  TrendingUp, PlayCircle, Lock, Unlock, Mail, Phone, MapPin,
  ExternalLink, Briefcase, GraduationCap, CheckCircle, AlertCircle,
  BarChart2, PieChart, Layers, Zap, Info, Clock, Download, Share2, Sparkles,
  MessageSquare, Mic, StopCircle, User, Shield, CreditCard, Layout,
  Loader, Copy, ArrowRight, ShieldCheck, Database, Sliders,
  FileEdit, CalendarDays, Wallet, UserPlus, Code2, MonitorPlay,
  Server, Upload, Edit, Globe, Map as MapIcon
} from 'lucide-react';
import { STUDENTS } from './data/students';
import { CLASSES } from './data/classes';
import { TESTS, TEST_RESULTS } from './data/tests';
import { ROLE_MATERIALS } from './data/jobRoleMaterials';
import { FUTURE_ROLES } from './data/futureRoles';
import { EdutorySection } from './components/certificates';
import StudentPortfolio from './components/StudentPortfolio';
import './styles/LandingPage.css';

// --- Constants & Roles ---
const ROLES = {
  STUDENT: 'student',
  COUNSELOR: 'counselor',
  ADMIN: 'admin'
};

// --- Standardized Mock Data ---
const RIA_QUESTIONS = [
  { id: 1, text: "I like to work on cars or machines", dimension: "R" },
  { id: 2, text: "I like to do complex puzzles and logic", dimension: "I" },
  { id: 3, text: "I am good at helping people with problems", dimension: "S" },
  { id: 4, text: "I like to lead groups and make decisions", dimension: "E" },
  { id: 5, text: "I like to keep detailed financial records", dimension: "C" },
  { id: 6, text: "I like to express myself artistically", dimension: "A" }
];

const BIG5_QUESTIONS = [
  { id: 'b1', text: "I see myself as extraverted, enthusiastic", dimension: "E" },
  { id: 'b2', text: "I see myself as critical, quarrelsome", dimension: "A" },
  { id: 'b3', text: "I see myself as dependable, self-disciplined", dimension: "C" },
  { id: 'b4', text: "I see myself as anxious, easily upset", dimension: "N" },
  { id: 'b5', text: "I see myself as open to new experiences, complex", dimension: "O" }
];

const SKILLS_QUESTIONS = [
  { id: 's1', text: "I can write code to solve problems", dimension: "Tech" },
  { id: 's2', text: "I am good at public speaking and presenting", dimension: "Comms" },
  { id: 's3', text: "I can analyze data trends using software", dimension: "Analysis" },
  { id: 's4', text: "I can manage project timelines effectively", dimension: "Management" }
];

const VALUES_QUESTIONS = [
  { id: 'v1', text: "It is important to help others in need", dimension: "Altruism" },
  { id: 'v2', text: "I value high earnings and financial success", dimension: "Wealth" },
  { id: 'v3', text: "I want a job where I can be creative", dimension: "Creativity" },
  { id: 'v4', text: "I prioritize work-life balance and flexibility", dimension: "Balance" }
];

const CAREER_LIST = [
  { id: '1', title: 'Software Architect', category: 'Technology', riasec: ['I', 'R'], skills: ['Cloud', 'System Design'], salary: '$140k - $220k', outlook: 'Very High', education: 'B.S. Computer Science' },
  { id: '2', title: 'Graphic Designer', category: 'Creative', riasec: ['A'], skills: ['Adobe CC', 'Typography'], salary: '$50k - $90k', outlook: 'Stable', education: 'BFA Design' },
  { id: '3', title: 'Clinical Psychologist', category: 'Healthcare', riasec: ['S', 'I'], skills: ['Empathy', 'Diagnosis'], salary: '$80k - $120k', outlook: 'High', education: 'PhD/PsyD' },
  { id: '4', title: 'Financial Analyst', category: 'Finance', riasec: ['C', 'E'], skills: ['Excel', 'Modeling'], salary: '$70k - $130k', outlook: 'High', education: 'B.S. Finance' },
  { id: '5', title: 'Data Scientist', category: 'Technology', riasec: ['I', 'C'], skills: ['Python', 'Statistics'], salary: '$110k - $190k', outlook: 'Very High', education: 'M.S. Data Science' },
];

const SCHOOLS_DATA = [
  { id: 'i1', name: 'University of Balamand', location: 'Koura, Lebanon', fees: '$7k/year', logo: 'UB' },
  { id: 'i2', name: 'American University of Beirut', location: 'Beirut, Lebanon', fees: '$15k/year', logo: 'AUB' },
  { id: 'i3', name: 'Lebanese American University', location: 'Beirut, Lebanon', fees: '$13k/year', logo: 'LAU' },
  { id: 'i4', name: 'Lebanese University', location: 'Beirut, Lebanon', fees: '$1k/year', logo: 'LU' }
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getTokens = (student) => {
  const parts = [
    ...(student.strengths || []),
    ...(student.interests || []),
    ...(student.weaknesses || [])
  ];
  return parts.map(item => item.toLowerCase());
};

const MAJOR_RULES = [
  {
    major: 'Computer Science',
    weights: { 'Computer Science': 1.2, Mathematics: 0.9 },
    keywords: ['programming', 'coding', 'robotics', 'computer', 'ai', 'technology', 'logic', 'problem', 'chess']
  },
  {
    major: 'Engineering',
    weights: { Mathematics: 1.0, Physics: 0.9, Chemistry: 0.4 },
    keywords: ['engineering', 'physics', 'math', 'mechanics']
  },
  {
    major: 'Medicine',
    weights: { Biology: 1.1, Chemistry: 0.9, Physics: 0.3 },
    keywords: ['medicine', 'biology', 'chemistry', 'health', 'research']
  },
  {
    major: 'Business & Finance',
    weights: { Mathematics: 0.8, English: 0.4, History: 0.3 },
    keywords: ['business', 'finance', 'economics', 'statistics', 'management', 'leadership']
  },
  {
    major: 'Law & International Relations',
    weights: { History: 1.0, English: 0.8, Geography: 0.5 },
    keywords: ['debate', 'writing', 'history', 'politics', 'government']
  },
  {
    major: 'Communications & Media',
    weights: { English: 0.9, History: 0.4, French: 0.4, Arabic: 0.4 },
    keywords: ['writing', 'journalism', 'media', 'public speaking', 'communication']
  },
  {
    major: 'Social Sciences',
    weights: { History: 0.6, Geography: 0.7, English: 0.4 },
    keywords: ['social', 'psychology', 'community', 'empathy']
  },
  {
    major: 'Arts & Design',
    weights: {},
    keywords: ['art', 'design', 'drawing', 'painting', 'photography', 'video', 'music', 'theater', 'creative']
  }
];

const RIASEC_KEYWORDS = {
  R: ['hands-on', 'mechanics', 'engineering', 'robotics', 'sports', 'technology', 'lab', 'physics'],
  I: ['math', 'science', 'research', 'analysis', 'programming', 'logic', 'problem', 'chemistry', 'biology', 'data'],
  A: ['art', 'design', 'music', 'writing', 'theater', 'poetry', 'photography', 'video', 'creative', 'drawing'],
  S: ['empathy', 'communication', 'help', 'social', 'volunteering', 'collaboration', 'teamwork'],
  E: ['leadership', 'debate', 'entrepreneur', 'business', 'management', 'public speaking', 'marketing'],
  C: ['organization', 'time management', 'detail', 'accounting', 'finance', 'structure', 'planning'],
};

const STUDENT_BY_ID = new Map(STUDENTS.map(student => [student.id, student]));
const CLASS_BY_ID = new Map(CLASSES.map(cls => [cls.id, cls]));
const TEST_BY_ID = new Map(TESTS.map(test => [test.id, test]));

const getStudentTestSummary = (studentId) => {
  const results = TEST_RESULTS.filter(result => result.studentId === studentId);
  if (results.length === 0) {
    return { overall: null, subjectAverages: [] };
  }

  const subjectTotals = {};
  const subjectCounts = {};
  let totalScore = 0;
  let totalMax = 0;

  results.forEach(result => {
    const test = TEST_BY_ID.get(result.testId);
    const classObj = test ? CLASS_BY_ID.get(test.classId) : null;
    const subject = classObj?.subject;

    totalScore += result.score;
    totalMax += result.maxScore;

    if (!subject) return;
    subjectTotals[subject] = (subjectTotals[subject] || 0) + result.percentage;
    subjectCounts[subject] = (subjectCounts[subject] || 0) + 1;
  });

  const subjectAverages = Object.keys(subjectTotals)
    .map(subject => ({
      subject,
      average: Math.round(subjectTotals[subject] / subjectCounts[subject])
    }))
    .sort((a, b) => b.average - a.average);

  const overall = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : null;

  return { overall, subjectAverages };
};

const getAggregateTestStats = () => {
  const subjectTotals = {};
  const subjectCounts = {};
  let totalScore = 0;
  let totalMax = 0;

  TEST_RESULTS.forEach(result => {
    const test = TEST_BY_ID.get(result.testId);
    const classObj = test ? CLASS_BY_ID.get(test.classId) : null;
    const subject = classObj?.subject;

    totalScore += result.score;
    totalMax += result.maxScore;

    if (!subject) return;
    subjectTotals[subject] = (subjectTotals[subject] || 0) + result.percentage;
    subjectCounts[subject] = (subjectCounts[subject] || 0) + 1;
  });

  const subjectAverages = Object.keys(subjectTotals)
    .map(subject => ({
      subject,
      average: Math.round(subjectTotals[subject] / subjectCounts[subject])
    }))
    .sort((a, b) => b.average - a.average);

  const overall = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : null;

  return { overall, subjectAverages, totalResults: TEST_RESULTS.length };
};

const getMajorDistribution = () => {
  const counts = {};
  STUDENT_INSTANCES.forEach(student => {
    const major = student.majorSuggestions?.primary || student.goal || 'Exploratory Studies';
    counts[major] = (counts[major] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([major, count]) => ({ major, count }))
    .sort((a, b) => b.count - a.count || a.major.localeCompare(b.major));
};

const getAtRiskStudents = () => {
  return STUDENT_INSTANCES.filter(student => {
    const profile = STUDENT_BY_ID.get(student.id);
    const overall = student.testSummary?.overall;
    const readiness = profile?.examReadiness ?? 0;
    const momentum = String(profile?.momentum || '').toLowerCase();
    const isDeclining = momentum.includes('declining');
    const lowTest = overall !== null ? overall < 60 : readiness < 60;
    return lowTest || isDeclining;
  });
};

const getLowConfidenceMatches = () => {
  const scored = STUDENT_INSTANCES.map(student => {
    const score = student.majorSuggestions?.suggestions?.[0]?.score ?? 0;
    return { student, score };
  });
  return scored.sort((a, b) => a.score - b.score);
};

const buildMajorSuggestions = (student, subjectAverages) => {
  const tokens = getTokens(student);
  const subjectMap = new Map(subjectAverages.map(item => [item.subject, item.average]));
  const hasSubjects = subjectAverages.length > 0;

  const scored = MAJOR_RULES.map(rule => {
    let score = 0;
    Object.entries(rule.weights).forEach(([subject, weight]) => {
      const avg = subjectMap.get(subject);
      if (avg !== undefined) {
        score += avg * weight;
      }
    });

    const keywordHits = rule.keywords.filter(keyword =>
      tokens.some(token => token.includes(keyword))
    ).length;

    score += keywordHits * (hasSubjects ? 6 : 12);

    return {
      major: rule.major,
      score,
      keywordHits
    };
  });

  const sorted = scored
    .sort((a, b) => b.score - a.score || b.keywordHits - a.keywordHits || a.major.localeCompare(b.major))
    .filter(item => item.score > 0);

  if (sorted.length === 0) {
    return { primary: 'Exploratory Studies', suggestions: [{ major: 'Exploratory Studies', score: 0 }], subjectAverages };
  }

  const suggestions = sorted.slice(0, 3);
  return { primary: suggestions[0].major, suggestions, subjectAverages };
};

const deriveTopCodes = (student) => {
  const tokens = getTokens(student);
  const scores = Object.fromEntries(Object.keys(RIASEC_KEYWORDS).map(code => [code, 0]));

  Object.entries(RIASEC_KEYWORDS).forEach(([code, keywords]) => {
    keywords.forEach(keyword => {
      if (tokens.some(token => token.includes(keyword))) {
        scores[code] += 1;
      }
    });
  });

  const ranked = Object.entries(scores)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([code]) => code);

  const defaults = ['I', 'R', 'C'];
  const top = ranked.filter(code => scores[code] > 0);
  const combined = [...top, ...defaults];
  return [...new Set(combined)].slice(0, 3);
};

const deriveBigFive = (student) => {
  const tokens = getTokens(student);
  const readiness = student.examReadiness ?? 60;
  const isDeclining = String(student.momentum || '').toLowerCase().includes('declining');
  const hasTestAnxiety = tokens.some(token => token.includes('test anxiety') || token.includes('anxiety'));
  const isSocial = tokens.some(token => token.includes('communication') || token.includes('public speaking') || token.includes('teamwork'));
  const isCreative = tokens.some(token => token.includes('creative') || token.includes('art') || token.includes('music'));

  return {
    O: clamp(40 + (student.interests?.length || 0) * 6 + (isCreative ? 10 : 0), 25, 95),
    C: clamp(30 + readiness * 0.6 - (tokens.some(token => token.includes('time management')) ? 10 : 0), 25, 95),
    E: clamp(30 + (isSocial ? 25 : 0) + (tokens.some(token => token.includes('leadership')) ? 10 : 0), 20, 95),
    A: clamp(35 + (tokens.some(token => token.includes('empathy')) ? 20 : 0) + (tokens.some(token => token.includes('collaboration')) ? 10 : 0), 20, 95),
    N: clamp(20 + (hasTestAnxiety ? 30 : 0) + (isDeclining ? 10 : 0), 10, 95),
  };
};

const STUDENT_INSTANCES = STUDENTS.map(student => {
  const testSummary = getStudentTestSummary(student.id);
  const majorSuggestions = buildMajorSuggestions(student, testSummary.subjectAverages);

  return {
    id: student.id,
    name: student.name,
    level: `High School (Grade ${student.grade}${student.section ? ` - ${student.section}` : ''})`,
    goal: majorSuggestions.primary,
    progress: clamp(Math.round(student.examReadiness ?? 0), 10, 99),
    topCodes: deriveTopCodes(student),
    bigFive: deriveBigFive(student),
    testSummary,
    majorSuggestions,
    // New Counselor Fields
    riskLevel: student.examReadiness < 60 ? 'High' : student.examReadiness < 80 ? 'Medium' : 'Low',
    supportStatus: 'Active',
    goals: [
      { id: 'g1', title: `Complete ${majorSuggestions.primary} Certification`, status: 'Approved', outcome: 'Certificate' },
      { id: 'g2', title: 'Find Summer Internship', status: 'Pending', outcome: 'Placement' }
    ],
    reflections: [
      { id: 'r1', date: 'Oct 20, 2025', topic: 'Midterm Review', text: 'Struggling with Physics, need more practice.' }
    ],
    counselorNotes: [
      { id: 'cn1', date: 'Sep 15, 2025', author: 'Counselor', text: 'Initial intake session. Student shows strong interest in STEM.' }
    ],
    actionPlan: [
      { id: 'ap1', task: 'Complete RIASEC assessment', status: 'Completed', dueDate: 'Oct 1, 2025' },
      { id: 'ap2', task: 'Review University list', status: 'In Progress', dueDate: 'Nov 15, 2025' }
    ]
  };
});

const ANALYTICS_DATA = [
  { name: 'Mon', revenue: 420, users: 120 }, { name: 'Tue', revenue: 380, users: 140 },
  { name: 'Wed', revenue: 550, users: 180 }, { name: 'Thu', revenue: 490, users: 160 },
  { name: 'Fri', revenue: 720, users: 210 }, { name: 'Sat', revenue: 210, users: 90 },
  { name: 'Sun', revenue: 150, users: 70 }
];

const SESSIONS_MOCK = [
  { id: 'sess1', student: 'Alex Johnson', time: '14:00 - 15:00', type: 'Career Strategy', status: 'Upcoming' },
  { id: 'sess2', student: 'Emma Watson', time: '10:00 - 11:00', type: 'Portfolio Review', status: 'Upcoming' },
  { id: 'sess3', student: 'Liam Neeson', time: '16:00 - 17:00', type: 'Mock Interview', status: 'Completed' },
];

const CONTENT_MOCK = [
  { id: 'm1', type: 'Video', title: 'Life as a Cloud Architect', duration: '12:40', author: 'TechDaily', views: '12k', tag: 'Tech' },
  { id: 'm2', type: 'Article', title: 'High-Growth Careers 2025', duration: '8 min read', author: 'GuidanceTeam', views: '45k', tag: 'Market' },
  { id: 'm3', type: 'Video', title: 'Admissions Masterclass', duration: '45:00', author: 'Dr. Mitchell', views: '5k', tag: 'Education' },
];

// --- Core Helper Components ---

const Badge = ({ children, variant = "default" }) => {
  const styles = {
    default: "bg-slate-100 text-slate-600",
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    red: "bg-red-50 text-red-600",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-widest ${styles[variant] || styles.default}`}>
      {children}
    </span>
  );
};

const Card = ({ children, className = "", onClick }) => {
  const interactive = Boolean(onClick);
  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      className={`glass-morphism rounded-2xl p-6 md:p-7 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 ${interactive ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-200' : ''} ${className}`}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

// --- Sub-View Components (Defined before App) ---

const buildRiaSecData = (student) => {
  const order = [
    { key: 'R', label: 'Realistic' },
    { key: 'I', label: 'Investigative' },
    { key: 'A', label: 'Artistic' },
    { key: 'S', label: 'Social' },
    { key: 'E', label: 'Enterprising' },
    { key: 'C', label: 'Conventional' },
  ];
  const base = { R: 55, I: 55, A: 55, S: 55, E: 55, C: 55 };
  const top = student?.topCodes || [];
  if (top[0]) base[top[0]] = 90;
  if (top[1]) base[top[1]] = 80;
  if (top[2]) base[top[2]] = 70;

  return order.map(item => ({ subject: item.label, A: base[item.key] }));
};

const StudentDashboard = ({ navigate, student }) => {
  if (!student) {
    return (
      <div className="glass-morphism rounded-lg p-6">
        <p className="text-slate-600">Student profile not available.</p>
      </div>
    );
  }

  const radarData = buildRiaSecData(student);
  const topMatch = student.majorSuggestions?.primary || student.goal || 'Exploratory Studies';
  const subjectHighlight = student.testSummary?.subjectAverages?.[0];
  const progressLabel = `${student.progress}% Sync`;
  const tasks = (STUDENT_BY_ID.get(student.id)?.weaknesses || []).slice(0, 2);
  const interestMatrixNote = `The interest matrix links ${student.name}'s core interests to real career clusters, keeping the roadmap aligned with their strengths and keeping motivation high.`;

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="text-left">
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Portfolio Hub 2025</h1>
          <p className="text-sm text-slate-500 font-medium">{topMatch} Path - {progressLabel}</p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6 text-left">
          <Card>
            <h3 className="text-[10px] font-black mb-6 flex items-center gap-2 text-slate-400 uppercase tracking-widest"><Activity className="text-indigo-600" size={14} /> Interest Matrix</h3>
            <p className="text-[11px] text-slate-500 font-semibold leading-relaxed mb-4">{interestMatrixNote}</p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="90%" data={radarData}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fontWeight: 800 }} />
                  <Radar name={student.name} dataKey="A" stroke="#4f46e5" strokeWidth={3} fill="#4f46e5" fillOpacity={0.55} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-4 space-y-6">
          <Card className="text-center relative overflow-hidden bg-indigo-50/10 border-indigo-100">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-violet-500" />
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-indigo-600 font-black text-xl mx-auto mb-3 border border-indigo-100 shadow-xl">
              {student.name.charAt(0)}
            </div>
            <h3 className="text-base font-black text-slate-900 tracking-tight leading-none uppercase">{student.name}</h3>
            <p className="text-slate-400 text-[8px] font-black uppercase tracking-[0.2em] mt-1.5 opacity-60">{student.level}</p>
            <div className="mt-8 space-y-3 text-left border-t pt-4">
              <div className="flex justify-between text-[9px] font-black text-slate-400 uppercase"><span>Roadmap Sync</span><span>{student.progress}%</span></div>
              <div className="h-1 bg-white rounded-full overflow-hidden flex shadow-inner">
                <div className="bg-emerald-500 h-full animate-pulse" style={{ width: `${student.progress}%` }} />
              </div>
            </div>
          </Card>
          <Card className="text-left">
            <h3 className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4 border-b pb-2">Focus Subjects</h3>
            <div className="space-y-3">
              {(student.testSummary?.subjectAverages || []).slice(0, 3).map(item => (
                <div key={item.subject} className="flex items-center justify-between gap-3">
                  <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">{item.subject}</span>
                  <span className="text-[11px] font-black text-emerald-600">{item.average}%</span>
                </div>
              ))}
              {!student.testSummary?.subjectAverages?.length && (
                <p className="text-[10px] text-slate-400 font-semibold">No subject averages available yet.</p>
              )}
            </div>
          </Card>
        </div>
      </div>

    </div>
  );
};
const AssessmentHub = ({ student }) => {
  const [activeQuiz, setActiveQuiz] = useState(null); // Changed from boolean to string/null
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [answers, setAnswers] = useState({});

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const generateAIPrompt = () => {
    if (!student) return "No student data found.";
    return `Act as an expert Career Counselor.
 Student Profile:
 - Name: ${student.name}
 - Grade: ${student.grade}
 - Top RIASEC Codes: ${student.topCodes.join(', ')}
 - Interests: ${student.interests.join(', ')}
 - Current Goal: ${student.goal}
 - Academic Progress: ${student.progress}%
 
 Please provide:
 1. A detailed analysis of their career potential based on this profile.
 2. 3 specific university majors that align with their strengths.
 3. Suggest 2 extracurricular activities to boost their portfolio.
 
 Format the response in a clear, encouraging tone.`;
  };

  const PROFILES = [
    { title: 'RIASEC Profile', description: 'Analyze interests to match with suitable careers using Holland Codes.' },
    { title: 'Big Five Profile', description: 'Understand personality traits, work style, and social dynamics.' },
    { title: 'Skills Profile', description: 'Evaluate technical competencies and soft skill strengths.' },
    { title: 'Values Profile', description: 'Align future career choices with personal core values and ethics.' }
  ];

  // Determine which questions to show based on active quiz title
  const getQuestions = (title) => {
    switch (title) {
      case 'RIASEC Profile': return RIA_QUESTIONS;
      case 'Big Five Profile': return BIG5_QUESTIONS;
      case 'Skills Profile': return SKILLS_QUESTIONS;
      case 'Values Profile': return VALUES_QUESTIONS;
      default: return [];
    }
  };

  const activeQuestions = activeQuiz ? getQuestions(activeQuiz) : [];

  const [showHistory, setShowHistory] = useState(null); // 'RIASEC Profile', etc.

  const handleSaveAssessment = () => {
    setIsSaving(true);
    // Simulate API latency
    setTimeout(() => {
      // Save to localStorage
      const storageKey = `career_app_assessment_history_${activeQuiz.replace(/\s+/g, '_')}`;
      const existingHistory = JSON.parse(localStorage.getItem(storageKey) || '[]');

      const newEntry = {
        id: Date.now(),
        date: new Date().toISOString(),
        answers: answers,
        result: {
          score: Math.floor(Math.random() * 20) + 80, // Mock score
          topCode: 'Analyzed',
          summary: `Assessment completed for ${activeQuiz}.`
        }
      };

      const newHistory = [newEntry, ...existingHistory];
      localStorage.setItem(storageKey, JSON.stringify(newHistory));

      setIsSaving(false);
      setSaveSuccess(true);
      setQuizResults(newEntry.result);
      setActiveQuiz(null); // Close quiz modal
    }, 1500);
  };

  if (activeQuiz && !saveSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-300">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 shadow-2xl border-indigo-100">
          <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">{activeQuiz}</h2>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Assessment In Progress</p>
            </div>
            <button onClick={() => setActiveQuiz(null)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20} /></button>
          </div>

          <div className="space-y-8">
            {activeQuestions.map((q) => (
              <div key={q.id} className="space-y-3">
                <p className="font-bold text-slate-700 text-sm leading-relaxed">{q.text}</p>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAnswers({ ...answers, [q.id]: val })}
                      className={`py-3 rounded-xl text-xs font-black transition-all ${answers[q.id] === val ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-105' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between px-1">
                  <span className="text-[9px] uppercase font-black text-slate-300 tracking-widest">Disagree</span>
                  <span className="text-[9px] uppercase font-black text-slate-300 tracking-widest">Agree</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
            <button
              onClick={handleSaveAssessment}
              disabled={Object.keys(answers).length < activeQuestions.length || isSaving}
              className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-black uppercase text-xs tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-50 disabled:shadow-none transition-all flex items-center gap-2"
            >
              {isSaving ? <Loader className="animate-spin" size={16} /> : <CheckCircle size={16} />}
              {isSaving ? 'Saving...' : 'Submit Assessment'}
            </button>
          </div>
        </Card>
      </div>
    );
  }

  if (showHistory) {
    const storageKey = `career_app_assessment_history_${showHistory.replace(/\s+/g, '_')}`;
    const history = JSON.parse(localStorage.getItem(storageKey) || '[]');

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex justify-between items-center mb-2">
          <div className="text-left">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">{showHistory} History</h1>
            <p className="text-[10px] uppercase font-black tracking-widest text-slate-400 mt-1">Past submissions archive</p>
          </div>
          <button onClick={() => setShowHistory(null)} className="text-xs font-bold text-slate-500 hover:text-indigo-600 flex items-center gap-2"><ChevronRight className="rotate-180" size={14} /> Back</button>
        </div>

        {history.length === 0 ? (
          <Card className="p-12 text-center bg-slate-50/50 border-dashed border-2 border-slate-200 shadow-none">
            <p className="text-sm font-bold text-slate-400">No history found for this assessment.</p>
          </Card>
        ) : (
          <div className="grid gap-4 text-left">
            {history.map((entry) => (
              <Card key={entry.id} className="group hover:border-indigo-300 transition-all border-slate-100">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-black text-slate-800 text-sm">{new Date(entry.date).toLocaleDateString()} at {new Date(entry.date).toLocaleTimeString()}</h3>
                    <p className="text-xs text-slate-500 mt-1">{entry.result.summary}</p>
                  </div>
                  <Badge variant="indigo">{entry.result.score}% Score</Badge>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-50 flex gap-2">
                  <div className="text-[10px] font-mono text-slate-400">ID: {entry.id}</div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (saveSuccess && quizResults) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-black text-slate-900 text-left tracking-tight uppercase">Psychometric Lab</h1>
          <button onClick={() => { setSaveSuccess(false); setAnswers({}); }} className="text-xs font-bold text-slate-500 hover:text-indigo-600">Back to Assessments</button>
        </div>
        <Card className="bg-emerald-50 border-emerald-100 p-8 text-center">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-emerald-500 mx-auto mb-4 shadow-lg"><CheckCircle size={32} /></div>
          <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Results Saved Successfully</h2>
          <p className="text-slate-500 text-xs font-medium mt-2 mb-6">Your results for {activeQuiz || 'the assessment'} have been saved to your history.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-black text-slate-900 text-left tracking-tight uppercase">Psychometric Lab</h1>
      </div>

      <Card className="bg-indigo-50/50 border-indigo-100 p-4 flex items-start gap-4 mb-6">
        <div className="bg-white p-2 rounded-lg text-indigo-600 shadow-sm"><Info size={20} /></div>
        <div>
          <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">Assessment Methodology</h4>
          <p className="text-[10px] text-slate-500 font-medium leading-relaxed mt-1">Our comprehensive suite maps your personality, skills, and values. Results are saved locally to your device.</p>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
        {PROFILES.map((p, i) => {
          const storageKey = `career_app_assessment_history_${p.title.replace(/\s+/g, '_')}`;
          const history = JSON.parse(localStorage.getItem(storageKey) || '[]');
          const latest = history[0]; // Get the most recent entry
          const isCompleted = !!latest;

          return (
            <Card
              key={p.title}
              className={`group flex flex-col items-center text-center transition-all border relative overflow-hidden ${isCompleted ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100 hover:border-indigo-600'}`}
            >
              {isCompleted && (
                <div className="absolute top-2 right-2">
                  <Badge variant="emerald" className="shadow-sm">{latest.result.score}%</Badge>
                </div>
              )}

              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110 ${isCompleted ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}`}>
                {isCompleted ? <CheckCircle size={20} /> : <Target size={20} />}
              </div>

              <h3 className={`text-sm font-black uppercase tracking-tight ${isCompleted ? 'text-emerald-900' : 'text-slate-800'}`}>{p.title}</h3>
              <p className="text-[10px] text-slate-400 mt-1 mb-6 font-medium leading-relaxed px-2 min-h-[40px]">{p.description}</p>

              <div className="w-full flex items-center justify-between gap-3 px-2">
                <button
                  onClick={() => { setActiveQuiz(p.title); setAnswers({}); }}
                  className={`flex-1 py-1.5 rounded-lg font-black text-[9px] uppercase tracking-widest transition-all ${isCompleted ? 'bg-white border border-emerald-200 text-emerald-600 hover:bg-emerald-50' : 'bg-slate-900 text-white hover:bg-indigo-600'}`}
                >
                  {isCompleted ? 'Retake' : 'Start'}
                </button>
                {isCompleted && (
                  <button
                    onClick={() => setShowHistory(p.title)}
                    className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-all"
                    title="View History"
                  >
                    <Activity size={14} />
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Moved from Dashboard: Career Recommendation */}
      <div className="mt-8">
        <Card className="bg-indigo-900 text-white border-0 shadow-2xl relative overflow-hidden group text-left">
          <div className="relative z-10">
            <Badge variant="indigo">2025 Smart Match</Badge>
            <h2 className="text-2xl font-black mt-2">{student.majorSuggestions?.primary || student.goal || 'Exploratory Studies'}</h2>
            <p className="text-indigo-100 text-xs mt-2 max-w-md opacity-80 leading-relaxed font-medium">
              {student.testSummary?.subjectAverages?.[0]
                ? `Top subject focus: ${student.testSummary.subjectAverages[0].subject} (${student.testSummary.subjectAverages[0].average}%).`
                : 'Match derived from strengths, interests, and overall performance.'}
            </p>
            <div className="flex gap-2 mt-6">
              <button className="px-4 py-1.5 bg-white text-indigo-900 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-indigo-50">Explore</button>
            </div>
          </div>
          <Sparkles className="absolute -right-4 -bottom-4 w-32 h-32 text-white opacity-10" />
        </Card>
      </div>
    </div>
  );
};

const MultimediaLibrary = () => (
  <div className="space-y-6 animate-in fade-in duration-500 text-left">
    <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase leading-none">Learning Center</h1>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {CONTENT_MOCK.map(m => (
        <Card key={m.id} className="p-0 overflow-hidden group border-0 shadow-md">
          <div className="h-32 bg-slate-900 flex items-center justify-center text-white/30 group-hover:bg-slate-800 transition-colors relative">
            <PlayCircle size={40} className="group-hover:scale-110 group-hover:text-white transition-all" />
            <div className="absolute top-2 left-2"><Badge variant="indigo">{m.tag}</Badge></div>
          </div>
          <div className="p-4 bg-white"><h4 className="font-bold text-xs leading-tight mb-2 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{m.title}</h4><p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{m.duration} • {m.views} views</p></div>
        </Card>
      ))}
    </div>
  </div>
);

const AcademicPaths = () => {
  const PATH_DETAILS = {
    'Science (PCM)': {
      summary: 'Engineering-first track with strong math + computing depth.',
      semesters: ['Adv. Calculus + Linear Algebra', 'Data Structures & Algorithms', 'Physics of Computing', 'Distributed Systems Lab'],
      outcomes: ['Ready for Software Architect pipeline', 'Competitive for CS/AI majors', 'Internship-ready by Year 2']
    },
    'Commerce': {
      summary: 'Business analytics core with finance and data fluency.',
      semesters: ['Financial Modeling', 'Statistics for Business', 'SQL + BI Dashboards', 'Operations & Supply Chain'],
      outcomes: ['Finance/FinTech pathways', 'Product/ops analytics roles', 'Prep for CFA L1 / FRM']
    },
    'Humanities': {
      summary: 'Communication, design, and policy mix with digital skills.',
      semesters: ['Behavioral Economics', 'Design Thinking', 'Media & Visual Comms', 'Policy & Ethics in Tech'],
      outcomes: ['UX/Content Strategy', 'Policy/Research fellowships', 'Strong writing + storytelling']
    }
  };

  const [selectedPath, setSelectedPath] = useState(null);
  const detail = selectedPath ? PATH_DETAILS[selectedPath] : null;

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500">
      <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Career Paths</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.keys(PATH_DETAILS).map(s => (
          <Card key={s} onClick={() => setSelectedPath(s)} className={`group hover:border-indigo-600 transition-all ${selectedPath === s ? 'border-indigo-200 shadow-lg' : ''}`}>
            <h3 className="text-base font-black text-slate-800 mb-2 uppercase tracking-tight">{s}</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed mb-6 font-medium">{PATH_DETAILS[s].summary}</p>
            <div className="w-full py-2 bg-slate-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-[0.2em] group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm text-center">
              Review Path
            </div>
          </Card>
        ))}
      </div>
      {detail && (
        <Card className="border-indigo-100 detail-animate">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1 space-y-2">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Semester Stack</h3>
              <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                {detail.semesters.map(item => <li key={item} className="flex items-start gap-2"><span className="w-2 h-2 mt-1 bg-indigo-500 rounded-full" />{item}</li>)}
              </ul>
            </div>
            <div className="flex-1 space-y-2">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Outcomes</h3>
              <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                {detail.outcomes.map(item => <li key={item} className="flex items-start gap-2"><span className="w-2 h-2 mt-1 bg-emerald-500 rounded-full" />{item}</li>)}
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const InstitutionsList = () => {
  const [selected, setSelected] = useState(null);
  const details = {
    'i1': { focus: 'AI/Systems Design', acceptance: 'Highly Selective', scholarships: 'Need-based + merit', campus: 'Palo Alto' },
    'i2': { focus: 'Robotics/ML', acceptance: 'Highly Selective', scholarships: 'Research assistantships', campus: 'Cambridge, MA' },
    'i3': { focus: 'Engineering Core', acceptance: 'Competitive', scholarships: 'Government + merit', campus: 'Mumbai' },
    'i4': { focus: 'Economics/Policy', acceptance: 'Selective', scholarships: 'Department grants', campus: 'London' },
  };
  const detail = selected ? details[selected.id] : null;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight text-left">University Network</h1>
      <Card className="bg-indigo-50 border-indigo-100 p-4 mb-4">
        <div className="flex items-start gap-3">
          <Sparkles className="text-indigo-600 mt-1 shrink-0" size={18} />
          <div>
            <h4 className="text-xs font-black text-indigo-900 uppercase tracking-tight">AI Recommendation Engine</h4>
            <p className="text-[10px] text-indigo-700/80 font-medium leading-relaxed mt-1">Based on your recent career analysis and projected major, these universities have been identified as the most suitable academic matches for your path.</p>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SCHOOLS_DATA.map(i => (
          <Card key={i.id} className={`flex gap-4 items-center group text-left cursor-pointer ${selected?.id === i.id ? 'border-indigo-200 shadow-lg' : ''}`} onClick={() => setSelected(i)}>
            <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center font-black text-indigo-600 text-xl group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-inner">{i.logo}</div>
            <div className="flex-1 min-w-0"><h3 className="font-bold text-sm text-slate-800 truncate uppercase tracking-tight">{i.name}</h3><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 opacity-70">{i.location}</p></div>
            <button className="p-2.5 bg-slate-50 rounded-xl text-indigo-600 group-hover:bg-indigo-500 group-hover:text-white transition-all shadow-sm"><ExternalLink size={16} /></button>
          </Card>
        ))}
      </div>
      {detail && (
        <Card className="border-indigo-100 detail-animate">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-16 h-16 bg-indigo-50 text-indigo-700 rounded-2xl flex items-center justify-center font-black text-xl border border-indigo-100">{selected.logo}</div>
            <div className="flex-1 space-y-2">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{selected.name}</h3>
              <p className="text-sm text-slate-500 font-semibold">{selected.location}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700 font-semibold">
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Academic Focus</p><p className="mt-1">{detail.focus}</p></div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Selectivity</p><p className="mt-1">{detail.acceptance}</p></div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Scholarships</p><p className="mt-1">{detail.scholarships}</p></div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Campus</p><p className="mt-1">{detail.campus}</p></div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const CareerExplorer = () => (
  <div className="space-y-6 text-left animate-in fade-in duration-500">
    <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Job Market</h1>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {CAREER_LIST.map(c => (
        <Card key={c.id} className="flex flex-col border-0 shadow-lg group">
          <div className="flex justify-between mb-4"><Badge variant="indigo">{c.category}</Badge><Badge variant="emerald">{c.outlook}</Badge></div>
          <h3 className="font-black text-lg text-slate-900 leading-tight group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{c.title}</h3>
          <p className="text-[9px] text-slate-400 mt-2 font-black tracking-widest uppercase opacity-60">RIASEC Analysis Active</p>
          <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between"><span className="text-base font-black text-emerald-600 tracking-tight">{c.salary.split(' ')[0]}</span><button className="p-1.5 bg-slate-900 text-white rounded-lg shadow-md hover:bg-indigo-600 transition-all"><ChevronRight size={16} /></button></div>
        </Card>
      ))}
    </div>
  </div>
);

const RoleMaterials = () => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(ROLE_MATERIALS[0]?.id || '');

  const filteredRoles = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return ROLE_MATERIALS;
    return ROLE_MATERIALS.filter(role => {
      const haystack = [
        role.title,
        role.category,
        role.summary,
        ...(role.skills || [])
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [query]);

  const activeRole = filteredRoles.find(role => role.id === selectedId) || filteredRoles[0] || null;

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Learning Resources</h1>
          <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Select a role to see courses, certifications, and references.</p>
        </div>
        <div className="relative w-full lg:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search roles, skills, or categories..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <Card className="p-0 overflow-hidden border-0 shadow-lg">
          <div className="p-4 bg-slate-50 border-b border-slate-100">
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Job Roles</p>
          </div>
          <div className="p-4 space-y-2 max-h-[520px] overflow-y-auto">
            {filteredRoles.length === 0 && (
              <p className="text-xs text-slate-400 font-semibold">No roles match your search.</p>
            )}
            {filteredRoles.map(role => (
              <button
                key={role.id}
                onClick={() => setSelectedId(role.id)}
                className={`w-full text-left px-4 py-3 rounded-xl border font-black uppercase tracking-tight text-xs transition-all ${activeRole?.id === role.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-100' : 'bg-white border-slate-100 text-slate-600 hover:border-indigo-200 hover:text-indigo-600'}`}
              >
                <div className="flex items-center justify-between">
                  <span>{role.title}</span>
                  <span className={`text-[9px] font-black uppercase tracking-widest ${activeRole?.id === role.id ? 'text-indigo-100' : 'text-slate-300'}`}>{role.category}</span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {activeRole ? (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="indigo">{activeRole.category}</Badge>
                  </div>
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mt-2">{activeRole.title}</h2>
                  <p className="text-sm text-slate-500 font-semibold mt-2">{activeRole.summary}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(activeRole.skills || []).map(skill => (
                      <Badge key={skill}>{skill}</Badge>
                    ))}
                  </div>
                </div>
                <button className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-black uppercase text-[9px] tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2">
                  <PlayCircle size={14} /> Start Track
                </button>
              </div>
            </Card>

            {/* Edutory Section */}
            <EdutorySection
              courses={activeRole.courses}
              certifications={activeRole.certifications}
            />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <Card className="border-0 shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-500">References</h3>
                  <Badge variant="purple">{activeRole.references.length}</Badge>
                </div>
                <div className="space-y-3">
                  {activeRole.references.map(item => (
                    <div key={item.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/60">
                      <div className="min-w-0">
                        <p className="text-xs font-black text-slate-800 uppercase tracking-tight truncate">{item.title}</p>
                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1">{item.provider}</p>
                      </div>
                      <a
                        href={item.url}
                        className="p-2 rounded-lg bg-white border border-slate-100 text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-colors"
                        aria-label={`Open ${item.title}`}
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="border-0 shadow-lg">
            <p className="text-sm text-slate-500 font-semibold">Select a role to view its materials.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

const RoadmapRoleCard = ({ role, isActive, onSelect }) => (
  <Card
    className={`border transition-all ${isActive ? 'border-indigo-200 bg-indigo-50/60 shadow-xl shadow-indigo-100/40' : 'border-slate-100 bg-white'}`}
    onClick={() => onSelect(role.id)}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">Role Track</p>
        <h3 className="text-lg font-black text-slate-900 tracking-tight mt-2">{role.name}</h3>
        <p className="text-xs text-slate-500 font-semibold mt-2 leading-relaxed">{role.summary}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
        <Briefcase size={18} />
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mt-4">
      {role.tags.map((tag) => (
        <span
          key={tag}
          className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${isActive ? 'bg-white text-indigo-600' : 'bg-slate-100 text-slate-600'}`}
        >
          {tag}
        </span>
      ))}
    </div>
    <div className="flex items-center justify-between mt-5 text-[9px] font-black uppercase tracking-widest text-slate-400">
      <span>{role.timeline}</span>
      <span className="text-slate-500">{role.salaryRange}</span>
    </div>
  </Card>
);



const DocumentLocker = () => {
  const [documents, setDocuments] = useState(['Resume_2025.pdf', 'AWS_Arch.png', 'Official_Transcripts.pdf']);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocuments(prev => [file.name, ...prev]);
    }
  };

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
      />
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">My Documents</h1>
        <button
          onClick={handleUploadClick}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-black uppercase text-[9px] tracking-widest shadow-lg flex items-center gap-2 hover:bg-indigo-700 transition-all"
        >
          <Upload size={12} /> Upload Evidence / Cert
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {documents.map(doc => (
          <Card key={doc} className="flex flex-col items-center text-center py-6 group cursor-pointer border-dashed border-2 hover:border-indigo-600 transition-all">
            <FileText className="text-slate-100 group-hover:text-indigo-600 transition-all duration-300 mb-4" size={32} />
            <p className="font-black text-slate-800 text-[10px] truncate w-full px-4 uppercase tracking-tighter">{doc}</p>
            <button className="mt-6 px-4 py-1 bg-slate-50 rounded-lg text-[9px] font-black uppercase text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">View</button>
          </Card>
        ))}
      </div>
    </div>
  );
};

const GoalAnalysisView = ({ goalTitle }) => {
  const [activeFilteredRoleId, setActiveFilteredRoleId] = useState(null);

  const filteredRoles = useMemo(() => {
    if (!goalTitle?.trim()) return FUTURE_ROLES;
    const term = goalTitle.toLowerCase();
    return FUTURE_ROLES.filter(role =>
      role.name.toLowerCase().includes(term) ||
      role.tags.some(tag => tag.toLowerCase().includes(term)) ||
      role.summary.toLowerCase().includes(term)
    );
  }, [goalTitle]);

  const activeFilteredRole = filteredRoles.find(r => r.id === activeFilteredRoleId) || filteredRoles[0] || null;

  useEffect(() => {
    setActiveFilteredRoleId(filteredRoles[0]?.id || null);
  }, [filteredRoles]);

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-12 animate-in slide-in-from-top-4 duration-500 text-left">
      <div className="space-y-12">
        <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight pl-4 border-l-4 border-indigo-600">Milestone Timeline</h3>
        <div className="pl-8 border-l-2 border-indigo-100 space-y-12 py-6 ml-4">
          {['Self Discovery', 'Foundation Mastery', 'Industry Placement'].map((p, i) => (
            <div key={p} className="relative group">
              <div className={`absolute -left-[49px] top-0 w-8 h-8 rounded-lg border-2 border-white shadow-lg flex items-center justify-center font-black text-[10px] ${i === 0 ? 'bg-emerald-500 text-white' : (i === 1 ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-300')}`}>
                {i + 1}
              </div>
              <div>
                <Badge variant={i === 0 ? 'emerald' : i === 1 ? 'indigo' : 'default'}>{i === 0 ? 'Completed' : (i === 1 ? 'Active' : 'Locked')}</Badge>
                <h3 className="font-black text-base mt-1 text-slate-800 uppercase tracking-tight">{p} Milestone</h3>
                <p className="text-slate-400 text-[10px] mt-2 max-w-lg leading-relaxed font-bold uppercase tracking-widest">Strategic professional deployment cycle.</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="space-y-6">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Future Role Roadmaps</h2>
            <p className="text-xs text-slate-500 font-semibold mt-2">
              {filteredRoles.length > 0
                ? 'Explore role-based learning paths matched to your goal.'
                : 'No specific roles found. Showing all tracks.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="indigo">Role Library</Badge>
            <Badge variant="emerald">{filteredRoles.length} Matches</Badge>
          </div>
        </div>

        {filteredRoles.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200 bg-slate-50 p-8 text-center">
            <p className="text-sm font-bold text-slate-400">No specific roles match "{goalTitle}". Showing general paths.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredRoles.map((role) => (
                <RoadmapRoleCard
                  key={role.id}
                  role={role}
                  isActive={role.id === activeFilteredRole?.id}
                  onSelect={setActiveFilteredRoleId}
                />
              ))}
            </div>

            {activeFilteredRole && (
              <Card className="border-indigo-100 bg-white animate-in fade-in duration-500">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">Roadmap Preview</p>
                    <h3 className="text-xl font-black text-slate-900 tracking-tight mt-1">{activeFilteredRole.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="indigo">{activeFilteredRole.timeline}</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 mt-4">
                  {activeFilteredRole.phases.map((phase) => (
                    <div key={phase.title} className="p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{phase.title}</p>
                      <ul className="mt-2 space-y-1.5">
                        {phase.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-[10px] text-slate-600 font-semibold">
                            <span className="w-1.5 h-1.5 mt-1 bg-indigo-500 rounded-full" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

const GoalSetter = () => {
  const [goals, setGoals] = useState([
    {
      id: 1,
      title: 'Software Architect',
      timeline: 'Dec 2026',
      stacks: ['Cloud-native (AWS/Azure/GCP)', 'Microservices', 'Event-driven', 'IaC (Terraform/CDK)'],
      milestones: ['Finish Advanced System Design', 'Lead 2 platform refactors', 'Ship multi-region rollout', 'Certify in cloud + security'],
      outcomes: ['Architect scalable services for 5M+ users', 'Mentor 3 engineers', 'Own reliability budgets <0.1% downtime']
    }
  ]);

  /* ... inside GoalSetter ... */
  const [isAdding, setIsAdding] = useState(false);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [activeDetails, setActiveDetails] = useState(1); // ID of expanded goal
  const [showNotification, setShowNotification] = useState(false);

  const handleAddGoal = () => {
    if (!newGoalTitle.trim()) return;
    const newGoal = {
      id: Date.now(),
      title: newGoalTitle,
      timeline: 'Planning Phase',
      stacks: [],
      milestones: [],
      outcomes: []
    };
    setGoals([newGoal, ...goals]);
    setNewGoalTitle('');
    setIsAdding(false);
    setActiveDetails(newGoal.id);

    // Trigger notification
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500 relative">
      {showNotification && (
        <div className="absolute top-0 right-0 z-50 animate-in slide-in-from-right fade-in duration-300">
          <div className="bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-xl flex items-center gap-3">
            <CheckCircle size={18} />
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest">Goal Added</p>
              <p className="text-xs font-semibold">The advisor team will contact you to setup and analyze this goal.</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Target Objectives</h1>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg font-black uppercase text-[9px] tracking-widest shadow-sm hover:border-indigo-600 hover:text-indigo-600 transition-all flex items-center gap-2"
        >
          {isAdding ? 'Cancel' : <><Plus size={12} /> Add Goal</>}
        </button>
      </div>

      {isAdding && (
        <Card className="bg-indigo-50 border-indigo-100 animate-in slide-in-from-top-4">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Define New Objective</h3>
            <p className="text-[9px] font-bold text-indigo-400/60 uppercase tracking-widest flex items-center gap-1"><Info size={10} /> Advisor team will contact you to setup & analyze</p>
          </div>
          <div className="flex gap-4">
            <input
              autoFocus
              placeholder="E.g. Senior Product Manager or Start a Business..."
              className="flex-1 p-3 rounded-xl border border-indigo-100 text-sm font-bold placeholder:font-medium focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={newGoalTitle}
              onChange={e => setNewGoalTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddGoal()}
            />
            <button
              onClick={handleAddGoal}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg hover:bg-indigo-700 transition-all"
            >
              Save
            </button>
          </div>
        </Card>
      )}

      {
        goals.map(goal => (
          <div key={goal.id} className="space-y-4">
            <Card className="border-0 shadow-lg bg-indigo-50/50 group hover:bg-indigo-50 transition-colors relative">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[9px] font-black uppercase text-indigo-400 tracking-[0.2em] mb-1">Active Objective</p>
                  <h2 className="text-2xl font-black text-indigo-900 tracking-tight leading-none uppercase">{goal.title}</h2>
                  <div className="flex gap-4 mt-2.5">
                    <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-1.5"><Calendar size={12} /> {goal.timeline}</span>
                  </div>
                  {goal.timeline === 'Planning Phase' && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg flex items-start gap-3">
                      <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Status: Pending Verification</p>
                        <p className="text-[11px] font-medium text-slate-600 mt-1 leading-relaxed">
                          The advisor team will contact you shortly to setup and analyze this new goal.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setActiveDetails(activeDetails === goal.id ? null : goal.id)}
                  className={`p-3 rounded-xl shadow-md cursor-pointer transition-all shrink-0 flex items-center gap-2 text-[11px] font-black uppercase tracking-wide ${activeDetails === goal.id ? 'bg-indigo-600 text-white shadow-indigo-200' : 'bg-white text-indigo-600 hover:scale-105'}`}
                >
                  {activeDetails === goal.id ? 'Hide Plan' : 'View Plan'} <Edit size={18} />
                </button>
              </div>
            </Card>

            {activeDetails === goal.id && (
              <Card className="border-indigo-100 bg-white detail-animate">
                <GoalAnalysisView goalTitle={goal.title} />
              </Card>
            )}
          </div>
        ))
      }
    </div >
  );
};

const StudentReflections = () => {
  const [entries, setEntries] = useState([
    { id: 1, date: 'Oct 24, 2025', topic: 'Physics Midterm', mood: 'Anxious', text: 'I felt really unprepared for the mechanics section. need to review forces.' },
    { id: 2, date: 'Oct 10, 2025', topic: 'Robotics Club', mood: 'Excited', text: 'We got the arm to move! realizing how much I love the hardware side of things.' }
  ]);
  const [isAdding, setIsAdding] = useState(false);
  const [newEntry, setNewEntry] = useState({ topic: '', mood: 'Neutral', text: '' });

  const handleAdd = () => {
    if (!newEntry.topic || !newEntry.text) return;
    setEntries([{ id: Date.now(), date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), ...newEntry }, ...entries]);
    setNewEntry({ topic: '', mood: 'Neutral', text: '' });
    setIsAdding(false);
  };

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Reflection Journal</h1>
        <button onClick={() => setIsAdding(!isAdding)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-black uppercase text-[9px] tracking-widest shadow-lg flex items-center gap-2 hover:bg-indigo-700 transition-all">
          {isAdding ? 'Cancel Entry' : <><Plus size={12} /> New Entry</>}
        </button>
      </div>

      {isAdding && (
        <Card className="bg-indigo-50 border-indigo-100 animate-in slide-in-from-top-4">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-4">New Reflection</h3>
          <div className="space-y-4">
            <input
              placeholder="Topic (e.g., Math Test, Career Fair)"
              className="w-full p-3 rounded-xl border border-indigo-100 text-sm font-bold placeholder:font-medium focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={newEntry.topic}
              onChange={e => setNewEntry({ ...newEntry, topic: e.target.value })}
            />
            <div className="flex gap-2">
              {['Excited', 'Confident', 'Neutral', 'Anxious', 'Frustrated'].map(m => (
                <button
                  key={m}
                  onClick={() => setNewEntry({ ...newEntry, mood: m })}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border ${newEntry.mood === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 text-slate-400 hover:border-indigo-300'}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <textarea
              placeholder="What's on your mind?"
              className="w-full p-3 h-32 rounded-xl border border-indigo-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
              value={newEntry.text}
              onChange={e => setNewEntry({ ...newEntry, text: e.target.value })}
            />
            <button onClick={handleAdd} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg hover:bg-indigo-700 transition-all">Save Reflection</button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {entries.map(e => (
          <Card key={e.id} className="group hover:border-indigo-200 transition-all">
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{e.date}</span>
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mt-1">{e.topic}</h3>
              </div>
              <Badge variant={['Excited', 'Confident'].includes(e.mood) ? 'emerald' : ['Anxious', 'Frustrated'].includes(e.mood) ? 'red' : 'default'}>{e.mood}</Badge>
            </div>
            <p className="text-xs text-slate-500 font-medium leading-relaxed mt-3">{e.text}</p>
          </Card>
        ))}
      </div>
    </div>
  );
};

const CounselorDashboardMain = () => {
  const featureCards = [
    {
      id: 'shadowing',
      title: 'Shadowing Roster',
      icon: Users,
      summary: 'Pair students to mentors and capture weekly reflections.',
      metrics: [{ l: 'Mentors', v: '8 active' }, { l: 'Pairings', v: '22 live' }],
      checklist: ['Auto-assign by skill gap', 'Send reflection prompts', 'Track mentor feedback'],
      outcomes: ['Placement-ready portfolios', 'Higher confidence score', 'Documented impact stories']
    },
    {
      id: 'drills',
      title: 'Interview Drills',
      icon: MonitorPlay,
      summary: 'Live mock loops with rubric scoring and video notes.',
      metrics: [{ l: 'Mocks', v: '12 this week' }, { l: 'Templates', v: '4 roles' }],
      checklist: ['Schedule live + async mocks', 'Share rubric + scores', 'Clip highlights for review'],
      outcomes: ['Improved technical depth', 'Behavioral answers sharpened', 'Benchmarked readiness']
    },
    {
      id: 'campaigns',
      title: 'Outreach Campaigns',
      icon: MessageSquare,
      summary: 'Targeted nudges to re-engage inactive learners.',
      metrics: [{ l: 'Campaigns', v: '3 active' }, { l: 'Open Rate', v: '42%' }],
      checklist: ['Segment by interest & risk', 'Automate reminders', 'Escalate low responders'],
      outcomes: ['Reduced churn risk', 'More booked sessions', 'Healthier engagement curves']
    },
    {
      id: 'interventions',
      title: 'Intervention Triggers',
      icon: AlertCircle,
      summary: 'Surface risk signals early and queue interventions.',
      metrics: [{ l: 'Flags', v: '5 hot' }, { l: 'SLA', v: '<24h' }],
      checklist: ['Monitor drop-off signals', 'Assign owner + action', 'Log outcomes + notes'],
      outcomes: ['Faster save-rate', 'Clear accountability', 'Audit-ready history']
    },
  ];

  const [selectedFeature, setSelectedFeature] = useState(null);

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Expert Performance</h1>
          <p className="text-sm text-slate-500 font-medium tracking-tight">Active monitoring for student instances.</p>
        </div>
        <div className="bg-white border border-slate-200 p-1.5 px-4 rounded-xl flex items-center gap-4 shadow-sm animate-in slide-in-from-right duration-700">
          <div className="flex flex-col text-right"><span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Projection</span><span className="text-base font-black text-emerald-600">$1,840.50</span></div>
          <button className="p-2 bg-emerald-50 text-emerald-600 rounded-lg shadow-inner"><Wallet size={18} /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-in slide-in-from-bottom-2 duration-700">
        {[{ l: 'Students', v: '24', c: 'indigo' }, { l: 'Rating', v: '4.9', c: 'amber' }, { l: 'Hours', v: '154h', c: 'purple' }, { l: 'Success', v: '92%', c: 'emerald' }].map(s => (
          <Card key={s.l}><p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{s.l}</p><p className={`text-xl font-black mt-1 text-${s.c}-600`}>{s.v}</p></Card>
        ))}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Counselor Feature Workspace</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Click a feature to view mock details</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {featureCards.map(card => {
            const Icon = card.icon;
            const isActive = selectedFeature?.id === card.id;
            return (
              <Card
                key={card.id}
                onClick={() => setSelectedFeature(card)}
                className={`${isActive ? 'border-indigo-200 shadow-lg bg-indigo-50/40' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-indigo-600'} shadow-inner`}><Icon size={18} /></div>
                  <div>
                    <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight">{card.title}</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Mock Feature</p>
                  </div>
                </div>
                <p className="text-[12px] text-slate-600 font-semibold mt-4 leading-relaxed">{card.summary}</p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  {card.metrics.map(m => (
                    <span key={m.l} className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-100 text-slate-600">{m.l}: {m.v}</span>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
        {selectedFeature && (
          <Card className="border-indigo-100 detail-animate">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 space-y-2">
                <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Playbook</h4>
                <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                  {selectedFeature.checklist.map(item => (
                    <li key={item} className="flex items-start gap-2"><span className="w-2 h-2 mt-1 bg-indigo-500 rounded-full" />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 space-y-2">
                <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Outcomes</h4>
                <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                  {selectedFeature.outcomes.map(item => (
                    <li key={item} className="flex items-start gap-2"><span className="w-2 h-2 mt-1 bg-emerald-500 rounded-full" />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 space-y-2">
                <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Metrics & Status</h4>
                <div className="grid grid-cols-2 gap-3">
                  {selectedFeature.metrics.map(m => (
                    <div key={m.l} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{m.l}</p>
                      <p className="text-base font-black text-slate-800 mt-1">{m.v}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="text-[10px] font-black mb-6 uppercase tracking-widest text-slate-400 border-b pb-2">Upcoming Pipeline</h3>
          <div className="space-y-2">
            {SESSIONS_MOCK.map(sess => (
              <div key={sess.id} className="p-3 border border-slate-50 bg-slate-50/30 rounded-xl flex justify-between items-center hover:bg-slate-50 transition-all group shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center font-black text-indigo-600 text-sm shadow-inner group-hover:bg-indigo-600 group-hover:text-white transition-all uppercase">{sess.student.charAt(0)}</div>
                  <div><p className="font-black text-slate-900 text-sm tracking-tight">{sess.student}</p><p className="text-[8px] text-slate-400 uppercase font-black tracking-tighter">{sess.time} • {sess.type}</p></div>
                </div>
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-[9px] font-black uppercase shadow-xl hover:bg-indigo-700 transition-all">Connect</button>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="text-[8px] font-black mb-6 uppercase tracking-widest text-slate-400 border-b pb-2">Velocity Radar</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ANALYTICS_DATA}><Area type="monotone" dataKey="users" stroke="#4f46e5" strokeWidth={3} fill="#4f46e5" fillOpacity={0.1} /></AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

const CounselorStudentsView = () => {
  const [selected, setSelected] = useState(STUDENT_INSTANCES[0]);
  const [activeTab, setActiveTab] = useState('overview');

  const handleGoalAction = (goalId, action) => {
    // Mock logic to update goal status
    const updatedGoals = selected.goals.map(g =>
      g.id === goalId ? { ...g, status: action === 'approve' ? 'Approved' : 'Revision Needed' } : g
    );
    setSelected({ ...selected, goals: updatedGoals });
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 text-left">
      <h1 className="text-3xl font-black uppercase tracking-tight">Caseload Registry</h1>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-3">
          {STUDENT_INSTANCES.map(s => (
            <button key={s.id} onClick={() => setSelected(s)} className={`w-full p-4 rounded-3xl flex items-center gap-4 text-left transition-all ${selected.id === s.id ? 'bg-indigo-600 text-white shadow-xl scale-105' : 'bg-white border border-slate-100 text-slate-600'}`}>
              <div className="relative">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black ${selected.id === s.id ? 'bg-white/20' : 'bg-indigo-50'}`}>{s.name.charAt(0)}</div>
                <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${s.riskLevel === 'High' ? 'bg-red-500' : s.riskLevel === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              </div>
              <div>
                <p className="font-black truncate text-sm uppercase tracking-tighter">{s.name}</p>
                <p className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${selected.id === s.id ? 'text-indigo-200' : 'text-slate-400'}`}>{s.supportStatus}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3">
          <Card className="h-full border-0 shadow-2xl p-8 rounded-[32px] overflow-hidden">
            <div className="flex justify-between items-start border-b border-slate-50 pb-6 mb-6">
              <div><h2 className="text-2xl font-black uppercase tracking-tight">{selected.name}</h2><p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">{selected.level} • {selected.goal}</p></div>
              <div className="flex gap-2">
                {['overview', 'goals', 'reflections', 'notes'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'overview' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-8">
                  <div className="space-y-6">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-[10px] tracking-widest"><Target size={18} className="text-indigo-600" /> RIASEC profile</h3>
                    <div className="flex flex-wrap gap-2">{selected.topCodes.map(code => <div key={code} className="w-12 h-12 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center text-indigo-700 font-black">{code}</div>)}</div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-[10px] tracking-widest"><FileEdit size={18} className="text-indigo-600" /> Pipeline</h3>
                    {['Market Insights 2025', 'Technical Matrix'].map(r => <div key={r} className="p-3 bg-slate-50 rounded-xl flex justify-between items-center text-xs font-bold shadow-sm">{r} <ExternalLink size={14} className="text-slate-300" /></div>)}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-[10px] tracking-widest"><Activity size={18} className="text-indigo-600" /> Test performance</h3>
                    {selected.testSummary?.overall !== null && selected.testSummary?.overall !== undefined ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-black uppercase tracking-widest text-slate-400">Overall</p>
                          <Badge variant="indigo">{selected.testSummary.overall}%</Badge>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${selected.testSummary.overall}%` }} />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selected.testSummary?.subjectAverages?.slice(0, 4).map(item => (
                            <span key={item.subject} className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-600">
                              {item.subject}: {item.average}%
                            </span>
                          )) || []}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 font-semibold">No test results available yet.</p>
                    )}
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-[10px] tracking-widest"><Sparkles size={18} className="text-indigo-600" /> AI major fit</h3>
                    <div className="space-y-2">
                      {selected.majorSuggestions?.suggestions?.map(item => (
                        <div key={item.major} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 shadow-sm">
                          <div>
                            <p className="text-xs font-black text-slate-800 uppercase tracking-tight">{item.major}</p>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Score {Math.round(item.score)}</p>
                          </div>
                          <Badge variant="emerald">Match</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'goals' && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-500 space-y-6">
                {selected.goals?.map(goal => (
                  <div key={goal.id} className="p-4 border border-slate-100 rounded-2xl flex justify-between items-center group hover:bg-slate-50 transition-colors">
                    <div>
                      <div className="flex items-center gap-3">
                        <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">{goal.title}</h4>
                        <Badge variant={goal.status === 'Approved' ? 'emerald' : goal.status === 'Revision Needed' ? 'red' : 'amber'}>{goal.status}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 font-medium mt-1">Target Outcome: {goal.outcome}</p>
                    </div>
                    {goal.status === 'Pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => handleGoalAction(goal.id, 'reject')} className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors" title="Request Revision"><X size={16} /></button>
                        <button onClick={() => handleGoalAction(goal.id, 'approve')} className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors" title="Approve Goal"><CheckCircle size={16} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'reflections' && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-500 grid gap-4">
                {selected.reflections?.map(ref => (
                  <div key={ref.id} className="p-4 bg-slate-50/50 border border-slate-100 rounded-2xl">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[9px] font-black uppercase text-indigo-400 tracking-widest">{ref.date}</span>
                      <h4 className="text-xs font-black text-slate-700 uppercase tracking-tight">{ref.topic}</h4>
                    </div>
                    <p className="text-xs text-slate-600 font-medium leading-relaxed italic">"{ref.text}"</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-500 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-[10px] tracking-widest"><FileEdit size={16} /> Session Notes</h3>
                  <div className="space-y-3 h-64 overflow-y-auto pr-2 custom-scrollbar">
                    {selected.counselorNotes?.map(note => (
                      <div key={note.id} className="p-3 bg-amber-50/50 border border-amber-100 rounded-xl">
                        <p className="text-[9px] font-black uppercase text-amber-600 tracking-widest mb-1">{note.date} • {note.author}</p>
                        <p className="text-xs text-slate-700 font-medium">{note.text}</p>
                      </div>
                    ))}
                  </div>
                  <textarea className="w-full p-3 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-100 resize-none h-24" placeholder="Add private session note..."></textarea>
                  <button className="w-full py-2 bg-slate-900 text-white rounded-lg font-black text-[9px] uppercase tracking-widest">Save Note</button>
                </div>
                <div className="space-y-4">
                  <h3 className="font-bold text-slate-700 flex items-center gap-2 uppercase text-[10px] tracking-widest"><Target size={16} /> Action Plan</h3>
                  <div className="space-y-2">
                    {selected.actionPlan?.map(task => (
                      <div key={task.id} className="flex items-center gap-3 p-3 border border-slate-100 rounded-xl bg-white">
                        <div className={`w-2 h-2 rounded-full ${task.status === 'Completed' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <div className="flex-1">
                          <p className="text-xs font-bold text-slate-800">{task.task}</p>
                          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Due {task.dueDate}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="flex items-center justify-center gap-2 w-full py-2 border-2 border-dashed border-slate-200 text-slate-400 rounded-xl font-bold text-[10px] uppercase tracking-widest hover:border-indigo-300 hover:text-indigo-500 transition-all"><Plus size={14} /> Add Task</button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};




const CounselorSessionManager = () => {
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [transcript, setTranscript] = useState('');
  const [analysisPrompt, setAnalysisPrompt] = useState(null);

  const students = STUDENT_INSTANCES;

  // Handle Walk-in Logic
  const selectedStudent = selectedStudentId === 'walkin'
    ? {
      name: 'Walk-in Candidate',
      grade: 'Guest',
      topCodes: ['N/A'],
      goal: 'Career Exploration'
    }
    : students.find(s => s.id === selectedStudentId);

  const generateAnalysis = () => {
    if (!selectedStudent || !transcript.trim()) return;

    const prompt = `Act as an expert Educational Psychologist and Career Counselor.

Student Context:
- Name: ${selectedStudent.name}
- Grade: ${selectedStudent.grade}
- RIASEC: ${selectedStudent.topCodes.join('/')}
- Goal: ${selectedStudent.goal}

Session Notes / Dialogue:
"${transcript}"

Please provide a structured analysis:

1. Observed Behavioral Traits
   - Analyze confidence, hesitation, enthusiasm, and communication style.
   - Flag any indicators of anxiety or high-potential leadership traits.

2. Detected Skills & Competencies
   - Extract hard and soft skills mentioned or demonstrated (e.g., logical reasoning, empathy, coding, public speaking).
   - Identify latent talents not yet formally recognized.

3. Core Emotional State & Needs
   - Identify underlying drivers (e.g., fear of failure, desire for impact, financial stability).

4. Recommended Pathway / Next Steps
   - Suggest 2-3 concrete career paths or majors based on this interaction.
   - Propose 1 immediate action item (e.g., "Take the Big Five Assessment").

Format as a professional clinical observation.`;

    setAnalysisPrompt(prompt);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 text-left h-[calc(100vh-140px)] flex flex-col">
      <div className="flex justify-between items-center shrink-0">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tight text-slate-900">Session Manager</h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Clinical Notes & AI Analysis</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value === 'walkin' ? 'walkin' : Number(e.target.value))}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl font-bold text-xs text-slate-700 focus:outline-none focus:border-indigo-500 shadow-sm"
          >
            <option value="">Select Student...</option>
            <option value="walkin" className="text-indigo-600 font-black">+ Walk-in / Guest</option>
            {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {selectedStudent && <Badge variant={selectedStudent.grade === 'Guest' ? 'amber' : 'indigo'}>{selectedStudent.grade}</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 flex-1 min-h-0">
        <Card className="flex flex-col border-0 shadow-xl bg-white h-full relative group">
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4 flex items-center gap-2"><FileEdit size={14} /> Session Notes</label>
          <textarea
            className="flex-1 w-full bg-slate-50 border-0 rounded-2xl p-6 resize-none outline-none font-medium leading-relaxed text-sm shadow-inner focus:ring-2 focus:ring-indigo-100 transition-all font-mono"
            placeholder="Record session notes, student concerns, or key discussion points here..."
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
          />
          <div className="absolute bottom-6 right-6">
            <button
              onClick={generateAnalysis}
              disabled={!selectedStudent || !transcript}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-indigo-200 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none transition-all flex items-center gap-2"
            >
              <Sparkles size={14} /> Generate AI Insights
            </button>
          </div>
        </Card>

        <Card className={`flex flex-col border-0 shadow-xl h-full transition-all duration-500 ${analysisPrompt ? 'bg-indigo-900 text-white' : 'bg-slate-50'}`}>
          <label className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2 ${analysisPrompt ? 'text-indigo-300' : 'text-slate-400'}`}><Activity size={14} /> AI Analysis Output</label>

          {analysisPrompt ? (
            <div className="flex-1 relative animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="absolute inset-0 bg-indigo-800/50 rounded-2xl p-6 overflow-y-auto custom-scrollbar border border-indigo-700/50">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-indigo-50">{analysisPrompt}</pre>
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(analysisPrompt)}
                className="absolute top-4 right-4 p-2 bg-indigo-500/20 hover:bg-indigo-500 text-white rounded-lg border border-indigo-400/30 transition-all"
                title="Copy Prompt"
              >
                <Copy size={16} />
              </button>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-300">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4"><Sparkles size={24} className="opacity-20" /></div>
              <p className="font-bold text-xs uppercase tracking-widest">Ready to Analyze</p>
              <p className="text-[10px] font-medium mt-2 max-w-xs text-center opacity-60">Select a student (or Walk-in), type your notes, and click generate to get AI-powered behavioral and skills insights.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
const CounselorFinancials = () => (
  <div className="space-y-10 animate-in fade-in duration-500 text-left">
    <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight leading-none">Financial Ledger</h1>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <Card className="bg-gradient-to-br from-indigo-700 to-indigo-900 text-white border-0 shadow-2xl p-10 rounded-[40px]"><p className="text-indigo-200 text-[10px] font-black uppercase tracking-[0.4em]">Total YTD Revenue</p><p className="text-5xl font-black mt-4 tracking-tighter">$14,520</p></Card>
      <Card className="p-10 shadow-xl border-0 rounded-[40px] bg-white"><p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.4em]">Pending Payout</p><p className="text-5xl font-black mt-4 text-amber-500 tracking-tighter">$840</p></Card>
      <Card className="p-10 shadow-xl border-0 rounded-[40px] bg-white"><p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.4em]">Tier</p><p className="text-5xl font-black mt-4 text-slate-800 tracking-tighter">Elite</p></Card>
    </div>
  </div>
);

const AdminGlobalDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([
    ...STUDENT_INSTANCES.map(s => ({ id: s.id, name: s.name, role: 'Student', status: 'Active', lastActive: '2 min ago' })),
    { id: 901, name: 'Sarah Connor', role: 'Counselor', status: 'Active', lastActive: '10 min ago' },
    { id: 902, name: 'Dr. Strange', role: 'Counselor', status: 'Away', lastActive: '2 days ago' },
  ]);
  const [contentItems, setContentItems] = useState([
    { id: 'c1', title: 'Software Architect Path', type: 'Career', status: 'Published', version: '2.1' },
    { id: 'c2', title: 'Data Science 101', type: 'Course', status: 'Draft', version: '1.0' },
    { id: 'c3', title: 'Exam Stress Guide', type: 'Resource', status: 'Published', version: '1.5' },
  ]);
  const [settings, setSettings] = useState({
    aiCoaching: true,
    strictPrivacy: false,
    autoArchive: true,
    maintenanceMode: false
  });

  const aggregate = getAggregateTestStats();
  const majors = getMajorDistribution();
  const atRisk = getAtRiskStudents();
  const topMajor = majors[0]?.major || 'Exploratory Studies';

  const metrics = [
    { l: 'Students Tracked', v: STUDENT_INSTANCES.length, c: 'indigo' },
    { l: 'Avg Test Score', v: aggregate.overall ? `${aggregate.overall}%` : 'N/A', c: 'purple' },
    { l: 'At-Risk Students', v: atRisk.length, c: 'amber' },
    { l: 'Top Major', v: topMajor, c: 'emerald' },
  ];

  const toggleSetting = (key) => setSettings(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-8 text-left animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Command Center</h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">System Administration & Observability</p>
        </div>
        <div className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl">
          <Server size={14} /> Secure Environment
        </div>
      </div>

      <div className="flex gap-2 p-1 bg-slate-100/50 rounded-xl w-fit">
        {[
          { id: 'overview', label: 'Overview', icon: Layout },
          { id: 'users', label: 'User Management', icon: Users },
          { id: 'content', label: 'Catalog', icon: Database },
          { id: 'settings', label: 'System', icon: Settings },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === tab.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <Icon size={14} /> {tab.label}
            </button>
          )
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {metrics.map(s => (
              <Card key={s.l} className="border-0 shadow-lg bg-white hover:-translate-y-1 transition-transform cursor-default text-left">
                <div className={`w-8 h-8 bg-${s.c}-50 text-${s.c}-600 rounded-xl flex items-center justify-center mb-3`}><TrendingUp size={16} /></div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{s.l}</p>
                <p className="text-2xl font-black mt-1 text-slate-800 tracking-tighter">{s.v}</p>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="h-64 flex flex-col justify-center items-center text-slate-400 border-dashed border-2 bg-slate-50/50">
              <Activity size={32} className="mb-4 opacity-50" />
              <p className="text-xs font-black uppercase tracking-widest">Real-time Traffic Monitor (Offline)</p>
            </Card>
            <div className="space-y-4">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">System Audit Log</h3>
              <div className="space-y-2">
                {[
                  { time: '10:42 AM', user: 'Admin', action: 'Updated Privacy Policy', status: 'Success' },
                  { time: '09:15 AM', user: 'System', action: 'Daily Backup', status: 'Success' },
                  { time: '08:30 AM', user: 'Counselor Sarah', action: 'Flagged Content', status: 'Pending' },
                ].map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl text-xs">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-slate-400">{log.time}</span>
                      <span className="font-bold text-slate-700">{log.user}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-slate-500">{log.action}</span>
                      <Badge variant={log.status === 'Success' ? 'emerald' : 'amber'}>{log.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="animate-in slide-in-from-right-4 duration-500 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">User Directory</h3>
            <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200">
              <UserPlus size={14} /> Add User
            </button>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Name', 'Role', 'Status', 'Last Active', 'Actions'].map(h => (
                    <th key={h} className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4 font-bold text-slate-700 text-xs">{user.name}</td>
                    <td className="px-6 py-4"><Badge variant={user.role === 'Admin' ? 'purple' : user.role === 'Counselor' ? 'indigo' : 'default'}>{user.role}</Badge></td>
                    <td className="px-6 py-4 font-bold text-xs"><span className={`inline-block w-2 h-2 rounded-full mr-2 ${user.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-400'}`}></span>{user.status}</td>
                    <td className="px-6 py-4 text-xs text-slate-500 font-medium">{user.lastActive}</td>
                    <td className="px-6 py-4">
                      <button className="text-slate-400 hover:text-indigo-600 font-bold text-[10px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'content' && (
        <div className="animate-in slide-in-from-right-4 duration-500 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Content Catalog</h3>
            <div className="flex gap-2">
              <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all">
                <Upload size={14} /> Import
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200">
                <Plus size={14} /> New Item
              </button>
            </div>
          </div>
          <div className="grid gap-3">
            {contentItems.map(item => (
              <div key={item.id} className="p-4 bg-white border border-slate-100 rounded-xl flex items-center justify-between hover:shadow-md transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><FileText size={18} /></div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">{item.title}</h4>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{item.type} • v{item.version}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant={item.status === 'Published' ? 'emerald' : 'amber'}>{item.status}</Badge>
                  <button className="p-2 text-slate-300 hover:text-indigo-600 transition-colors"><Edit size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="animate-in slide-in-from-right-4 duration-500 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><Sparkles size={20} /></div>
              <div>
                <h3 className="font-black text-slate-800 uppercase tracking-tight text-sm">AI Configuration</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Model parameters & restrictions</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div><p className="text-xs font-bold text-slate-700">Enable AI Coaching</p><p className="text-[10px] text-slate-400 font-medium">Allow students to generate insights</p></div>
                <button onClick={() => toggleSetting('aiCoaching')} className={`w-10 h-5 rounded-full transition-colors relative ${settings.aiCoaching ? 'bg-indigo-600' : 'bg-slate-300'}`}><div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${settings.aiCoaching ? 'left-6' : 'left-1'}`} /></button>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div><p className="text-xs font-bold text-slate-700">Strict Privacy Mode</p><p className="text-[10px] text-slate-400 font-medium">Redact PII from AI prompts</p></div>
                <button onClick={() => toggleSetting('strictPrivacy')} className={`w-10 h-5 rounded-full transition-colors relative ${settings.strictPrivacy ? 'bg-indigo-600' : 'bg-slate-300'}`}><div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${settings.strictPrivacy ? 'left-6' : 'left-1'}`} /></button>
              </div>
            </div>
          </Card>

          <Card className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center"><Server size={20} /></div>
              <div>
                <h3 className="font-black text-slate-800 uppercase tracking-tight text-sm">System Controls</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Maintenance & Persistence</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div><p className="text-xs font-bold text-slate-700">Auto-Archive Inactive</p><p className="text-[10px] text-slate-400 font-medium">Archive users after 1 year</p></div>
                <button onClick={() => toggleSetting('autoArchive')} className={`w-10 h-5 rounded-full transition-colors relative ${settings.autoArchive ? 'bg-indigo-600' : 'bg-slate-300'}`}><div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${settings.autoArchive ? 'left-6' : 'left-1'}`} /></button>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div><p className="text-xs font-bold text-slate-700">Maintenance Mode</p><p className="text-[10px] text-slate-400 font-medium">Restrict access to admins only</p></div>
                <button onClick={() => toggleSetting('maintenanceMode')} className={`w-10 h-5 rounded-full transition-colors relative ${settings.maintenanceMode ? 'bg-indigo-600' : 'bg-slate-300'}`}><div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${settings.maintenanceMode ? 'left-6' : 'left-1'}`} /></button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const AdminOutcomesPanel = () => {
  const aggregate = getAggregateTestStats();
  const majors = getMajorDistribution();
  const atRisk = getAtRiskStudents();
  const lowConfidence = getLowConfidenceMatches();

  const topSubjects = aggregate.subjectAverages.slice(0, 6);
  const lowConfidenceAvg = lowConfidence.length
    ? Math.round(lowConfidence.reduce((sum, item) => sum + item.score, 0) / lowConfidence.length)
    : 0;

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Outcomes & AI Oversight</h1>
          <p className="text-sm text-slate-500 font-medium">Monitor major fit, performance signals, and AI confidence.</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="indigo">{aggregate.totalResults} test results</Badge>
          <Badge variant="emerald">{majors.length} majors tracked</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-[0.2em] mb-6">Subject Performance</h3>
          {topSubjects.length > 0 ? (
            <div className="space-y-4">
              {topSubjects.map(item => (
                <div key={item.subject} className="space-y-2">
                  <div className="flex justify-between text-[11px] font-black text-slate-600 uppercase tracking-widest">
                    <span>{item.subject}</span>
                    <span>{item.average}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${item.average}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 font-semibold">No test data available yet.</p>
          )}
        </Card>

        <Card>
          <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-widest mb-6">AI Confidence</h3>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Avg confidence score</p>
              <p className="text-2xl font-black text-slate-800 mt-2">{lowConfidenceAvg}</p>
            </div>
            <div className="space-y-3">
              {lowConfidence.slice(0, 5).map(item => (
                <div key={item.student.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div>
                    <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight">{item.student.name}</p>
                    <p className="text-[9px] text-slate-400 uppercase tracking-widest">Score {Math.round(item.score)}</p>
                  </div>
                  <Badge variant="amber">Review</Badge>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-[0.2em] mb-6">Major Distribution</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {majors.map(item => (
              <div key={item.major} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs font-black text-slate-700 uppercase tracking-tight">{item.major}</p>
                <Badge variant="indigo">{item.count}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-widest mb-6">At-Risk Signals</h3>
          <div className="space-y-3">
            {atRisk.slice(0, 6).map(student => (
              <div key={student.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div>
                  <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight">{student.name}</p>
                  <p className="text-[9px] text-slate-400 uppercase tracking-widest">{student.testSummary?.overall ?? 'N/A'}% overall</p>
                </div>
                <Badge variant="red">Flag</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

// --- Auth Component - Premium Landing Page ---

const LoginScreen = ({ onLogin }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const roleData = [
    { role: ROLES.STUDENT, icon: <User size={24} />, title: 'Student Portal', subtitle: 'Discover Your Path' },
    { role: ROLES.COUNSELOR, icon: <Users size={24} />, title: 'Counselor Portal', subtitle: 'Guide & Support' },
    { role: ROLES.ADMIN, icon: <Shield size={24} />, title: 'Admin Portal', subtitle: 'Manage & Monitor' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please fill in all fields');
      return;
    }
    const success = onLogin(selectedRole, username, password);
    if (!success) {
      setError('Invalid username or password');
    }
  };

  return (
    <div className="landing-container">
      <div className="landing-card">
        {/* Left Panel */}
        <div className="landing-hero">
          <div>

            <div className="landing-hero-content">
              <img src="/logo_edutory.png" alt="Edutory" className="w-64 h-auto mb-4" />
              <p className="landing-subtitle">Empowering students with intelligent career guidance and personalized learning pathways.</p>
              <span className="landing-edition">Career Guidance - Edition 2025</span>
            </div>
          </div>
          <div className="landing-footer">
            <a href="https://www.code2craft.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 font-bold uppercase tracking-widest text-[10px] hover:text-indigo-600 transition-colors">
              <div className="bg-slate-100 p-1 rounded-md group-hover:bg-indigo-100 transition-colors"><Code2 size={14} /></div>
              Powered By Code2Craft
            </a>
            <div className="landing-footer-line" />
          </div>
        </div>

        {/* Right Panel */}
        <div className="landing-login">
          <div className="landing-login-header">
            <h2 className="landing-login-title">{selectedRole ? 'Sign In' : 'Welcome Back'}</h2>
            <p className="landing-login-description">{selectedRole ? `Access your ${selectedRole} dashboard` : 'Select your portal to continue'}</p>
          </div>

          {!selectedRole ? (
            <>
              <div className="landing-features">
                <div className="landing-feature"><div className="landing-feature-icon"><Target size={20} /></div><p className="landing-feature-text">Smart Goals</p></div>
                <div className="landing-feature"><div className="landing-feature-icon"><Award size={20} /></div><p className="landing-feature-text">Certifications</p></div>
                <div className="landing-feature"><div className="landing-feature-icon"><TrendingUp size={20} /></div><p className="landing-feature-text">Track Progress</p></div>
              </div>
              <div className="landing-roles">
                {roleData.map((item) => (
                  <button key={item.role} onClick={() => setSelectedRole(item.role)} className="landing-role-button">
                    <div className="landing-role-icon">{item.icon}</div>
                    <div className="landing-role-content"><p className="landing-role-title">{item.title}</p><p className="landing-role-subtitle">{item.subtitle}</p></div>
                    <ChevronRight className="landing-role-arrow" size={24} />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 px-8 mt-8">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="••••••••"
                />
              </div>

              {error && <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl flex items-center gap-2"><LogOut size={12} /> {error}</div>}

              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => { setSelectedRole(null); setError(''); setUsername(''); setPassword(''); }} className="px-4 py-3 bg-slate-100 text-slate-500 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all">Back</button>
                <button type="submit" className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200">Login</button>
              </div>
            </form>
          )}

          <div className="landing-login-footer">
            <p className="landing-copyright">Intelligence Framework © 2025</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main App Implementation ---

const App = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [systemSettings, setSystemSettings] = useState({
    aiRecommendations: true,
    autoSyncTests: true,
    atRiskAlerts: true,
    counselorDigest: false,
  });
  const studentProfile = user?.role === ROLES.STUDENT
    ? STUDENT_INSTANCES.find(student => student.id === user.id)
    : null;
  const adminStats = useMemo(() => {
    const atRisk = getAtRiskStudents();
    return {
      students: STUDENT_INSTANCES.length,
      counselors: 1,
      admins: 1,
      atRisk: atRisk.length
    };
  }, []);

  const toggleSetting = (key) => {
    setSystemSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const navigation = useMemo(() => {
    if (!user) return [];
    const baseNav = {
      [ROLES.STUDENT]: [
        { id: 'dashboard', label: 'Portfolio', icon: Layout },
        { id: 'assessment', label: 'Assessments', icon: Target },
        { id: 'recommendations', label: 'Career Recommendations', icon: Sparkles },
        { id: 'goals', label: 'Career Goals', icon: Briefcase },


        { id: 'institutions', label: 'Universities', icon: Globe },

        { id: 'role-materials', label: 'Resources', icon: Award },

        { id: 'reflections', label: 'Reflections', icon: BookOpen },
        { id: 'locker', label: 'My Documents', icon: Shield },
      ],
      [ROLES.COUNSELOR]: [
        { id: 'c-dashboard', label: 'Expert Hub', icon: Layout },
        { id: 'c-students', label: 'Caseload', icon: Users },
        { id: 'c-notes', label: 'Session Manager', icon: FileEdit },
        { id: 'c-earnings', label: 'Financial Ledger', icon: Wallet },
        { id: 'c-availability', label: 'Sync Gateway', icon: CalendarDays },
      ],
      [ROLES.ADMIN]: [
        { id: 'a-dashboard', label: 'Control', icon: ShieldCheck },
        { id: 'a-outcomes', label: 'Outcomes', icon: TrendingUp },
        { id: 'a-users', label: 'User Hub', icon: Users },
        { id: 'a-cms', label: 'Database', icon: Database },
        { id: 'a-settings', label: 'Settings', icon: Settings },
      ]
    };
    return baseNav[user.role] || [];
  }, [user]);

  const handleLogin = (role, username, password) => {
    let isValid = false;
    let userId = '1';
    let name = '';

    if (role === ROLES.STUDENT) {
      if (username === 'rayan.saab' && password === '3566') {
        const student = STUDENT_INSTANCES.find(s => s.id === 52) || STUDENT_INSTANCES[0];
        userId = student ? student.id : '1';
        name = student ? student.name : 'Student';
        isValid = true;
      }
    } else if (role === ROLES.COUNSELOR) {
      if (username === 'fadi.maamari' && password === '3566') {
        name = 'Mr. Fadi Maamari';
        isValid = true;
      }
    } else if (role === ROLES.ADMIN) {
      if (username === 'fadi.maamari' && password === '3566') {
        name = 'Mr. Fadi Maamari';
        isValid = true;
      }
    }

    if (isValid) {
      setUser({ id: userId, name, role });
      setIsAuthenticated(true);
      setActiveTab(role === ROLES.STUDENT ? 'dashboard' : (role === ROLES.COUNSELOR ? 'c-dashboard' : 'a-dashboard'));
      return true;
    } else {
      return false;
    }
  };
  if (!isAuthenticated) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-transparent flex flex-col font-sans text-slate-900 select-none overflow-hidden">
      <nav className="h-16 glass-morphism flex items-center justify-between px-6 sticky top-0 z-50 shadow-sm animate-in fade-in duration-1000">
        <div className="flex items-center gap-4 text-left">
          <div className="text-left">
            <img src="/logo_edutory.png" alt="Edutory" className="h-10 w-auto" />

          </div>
        </div>
        <div className="flex items-center gap-6">
          <Badge variant="indigo">{user.role} Portal</Badge>
          <div className="h-8 w-[1px] bg-slate-100" />
          <div className="flex items-center gap-4 text-left">
            <div className="text-right hidden sm:block"><p className="text-[11px] font-black text-slate-900 tracking-tight leading-none uppercase">{user.name}</p><button onClick={() => setIsAuthenticated(false)} className="text-[8px] font-black text-red-500 uppercase tracking-widest mt-1 hover:underline">Disconnect</button></div>
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 shadow-xl shadow-indigo-50/20 flex items-center justify-center font-black text-indigo-600 text-base transition-transform hover:scale-110 active:scale-95 cursor-pointer uppercase">{user.name.charAt(0)}</div>
          </div>
        </div>
      </nav>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 glass-morphism p-4 flex flex-col hidden lg:flex overflow-y-auto animate-in slide-in-from-left duration-1000 shadow-sm">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] mb-6 ml-4 opacity-60 shrink-0">2025 Architecture</p>
          {navigation.map(item => (
            <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center gap-4 px-6 py-3 rounded-2xl font-black transition-all duration-300 group justify-start text-left ${activeTab === item.id ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100 scale-105' : 'text-slate-500 hover:bg-slate-50 hover:text-indigo-600'}`}>
              <item.icon size={18} className={`${activeTab === item.id ? 'text-white' : 'text-slate-300 group-hover:text-indigo-600'} transition-colors`} /><span className="text-xs tracking-tight">{item.label}</span>
              {activeTab === item.id && <ChevronRight size={14} className="ml-auto opacity-50 animate-in fade-in slide-in-from-left duration-500" />}
            </button>
          ))}
          <div className="mt-auto pt-6 px-6">
            <a href="https://www.code2craft.com" target="_blank" rel="noopener noreferrer" className="flex flex-col gap-1.5 group transition-all">
              <div className="flex items-center gap-2 text-slate-500 font-bold uppercase tracking-widest text-[10px] group-hover:text-indigo-600 transition-colors">
                <Code2 size={16} /> Powered By
              </div>
              <span className="text-xs font-black text-indigo-600 tracking-tight uppercase ml-6">Code2Craft</span>
              <span className="text-[10px] font-medium text-slate-500 ml-6 tracking-wide group-hover:text-indigo-500">www.code2craft.com</span>
            </a>
          </div>
        </aside>

        <main key={activeTab} className="flex-1 p-6 lg:p-10 overflow-y-auto w-full h-[calc(100vh-64px)] bg-transparent scroll-smooth animate-in fade-in slide-in-from-right-4 duration-700">
          {activeTab === 'dashboard' && <StudentDashboard navigate={setActiveTab} student={studentProfile} />}
          {activeTab === 'assessment' && <AssessmentHub student={studentProfile} />}
          {activeTab === 'recommendations' && <StudentPortfolio student={studentProfile} testResults={TEST_RESULTS} tests={TESTS} />}


          {activeTab === 'institutions' && <InstitutionsList />}

          {activeTab === 'role-materials' && <RoleMaterials />}

          {activeTab === 'locker' && <DocumentLocker />}
          {activeTab === 'reflections' && <StudentReflections />}
          {activeTab === 'goals' && <GoalSetter />}
          {activeTab === 'c-dashboard' && <CounselorDashboardMain />}
          {activeTab === 'c-students' && <CounselorStudentsView />}
          {activeTab === 'c-notes' && <CounselorSessionManager />}

          {activeTab === 'c-earnings' && <CounselorFinancials />}
          {activeTab === 'c-availability' && (
            <div className="animate-in fade-in duration-500 text-left h-full">
              <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-8">Synchronizer Gateway</h1>
              <Card className="h-96 flex flex-col items-center justify-center text-center border-4 border-dashed border-slate-200 rounded-[48px] bg-slate-50/30">
                <CalendarDays size={48} className="text-indigo-100 mb-6 shadow-indigo-100" />
                <h3 className="text-xl font-bold uppercase tracking-tight text-slate-800">Operational Slot Automation</h3>
                <p className="text-slate-500 max-w-sm mt-3 text-sm font-medium leading-relaxed">Automate availability blocks for the 2025 consultation calendar.</p>
                <button className="mt-8 px-10 py-3.5 bg-indigo-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all">Link External Cloud</button>
              </Card>
            </div>
          )}
          {activeTab === 'a-dashboard' && <AdminGlobalDashboard />}
          {activeTab === 'a-outcomes' && <AdminOutcomesPanel />}
          {activeTab === 'a-users' && (
            <div className="space-y-8 animate-in fade-in duration-500 text-left">
              <div className="flex justify-between items-center">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">User Hub</h1>
                <button className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-black uppercase text-[9px] tracking-widest shadow-lg shadow-indigo-100 flex items-center gap-2"><UserPlus size={16} /> Provision ID</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { l: 'Students', v: adminStats.students, c: 'indigo' },
                  { l: 'Counselors', v: adminStats.counselors, c: 'purple' },
                  { l: 'Admins', v: adminStats.admins, c: 'emerald' },
                  { l: 'At Risk', v: adminStats.atRisk, c: 'amber' },
                ].map(item => (
                  <Card key={item.l} className="border-0 shadow-lg bg-white">
                    <div className={`w-8 h-8 bg-${item.c}-50 text-${item.c}-600 rounded-xl flex items-center justify-center mb-3`}>
                      <TrendingUp size={16} />
                    </div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.l}</p>
                    <p className="text-xl font-black text-slate-800">{item.v}</p>
                  </Card>
                ))}
              </div>
              <Card className="p-0 overflow-hidden border-0 shadow-xl rounded-[32px]">
                <div className="p-6 space-y-4">
                  {[...STUDENT_INSTANCES, { id: 'c1', name: 'Dr. Sarah Mitchell', level: 'Master Expert', goal: 'Counselor Lead', progress: 90, topCodes: ['S', 'E', 'A'] }].map(u => (
                    <div
                      key={u.id}
                      onClick={() => setSelectedAdminUser(u)}
                      className={`p-4 border border-slate-50 rounded-2xl flex justify-between items-center hover:bg-slate-50 transition-all cursor-pointer group ${selectedAdminUser?.id === u.id ? 'bg-indigo-50/50 border-indigo-200' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black transition-all duration-300 shadow-inner ${selectedAdminUser?.id === u.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white'}`}>{u.name.charAt(0)}</div>
                        <div>
                          <p className="font-black text-slate-900 text-sm tracking-tight leading-none uppercase">{u.name}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge>{u.level}</Badge>
                            <Badge variant="indigo">{u.goal || 'Career Path'}</Badge>
                            {u.testSummary?.overall != null && <Badge variant="emerald">{u.testSummary.overall}%</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest bg-slate-900 text-white rounded-lg hover:bg-black transition-all">Review</button>
                        <button className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"><Edit size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              {selectedAdminUser ? (
                <Card className="border-indigo-100 detail-animate">
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-1 space-y-2">
                      <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{selectedAdminUser.name}</h3>
                      <p className="text-sm text-slate-500 font-semibold">{selectedAdminUser.level || 'Student'}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="indigo">{selectedAdminUser.goal || 'Portfolio Track'}</Badge>
                        <Badge variant="emerald">{(selectedAdminUser.progress || 72) + '%'} Sync</Badge>
                      </div>
                    </div>
                    <div className="flex-1 space-y-3">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Top Codes</p>
                      <div className="flex gap-2 flex-wrap">
                        {(selectedAdminUser.topCodes || ['I', 'R', 'C']).map(code => (
                          <span key={code} className="w-10 h-10 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center text-indigo-700 font-black text-sm">{code}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 space-y-3">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Progress</p>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                        <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${Math.min(100, selectedAdminUser.progress || 72)}%` }} />
                      </div>
                      <p className="text-xs text-slate-500 font-semibold mt-1">Mock dashboard snapshot for admin view.</p>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card className="bg-slate-50 text-slate-500 detail-animate">
                  <p className="text-sm font-semibold">Select a user to preview their dashboard summary.</p>
                </Card>
              )}
            </div>
          )}
          {activeTab === 'a-cms' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in duration-500 text-left">
              <Card className="p-8 rounded-[32px] shadow-xl border-0 bg-white">
                <h3 className="text-lg font-black mb-8 flex items-center justify-between uppercase tracking-widest text-slate-800">Career Library <Plus size={20} className="text-indigo-600 cursor-pointer" /></h3>
                <div className="space-y-4">
                  {CAREER_LIST.map(c => (
                    <div key={c.id} className="p-4 bg-slate-50 rounded-2xl flex items-center justify-between border border-transparent hover:border-indigo-100 transition-all shadow-sm">
                      <div>
                        <p className="font-black text-xs text-slate-800 uppercase tracking-tight">{c.title}</p>
                        <p className="text-[9px] text-slate-400 uppercase tracking-widest">{c.category} • {c.education}</p>
                      </div>
                      <button className="p-2 text-slate-300 hover:text-indigo-600 transition-colors"><Edit size={14} /></button>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-8 rounded-[32px] shadow-xl border-0 bg-white">
                <h3 className="text-lg font-black mb-8 flex items-center justify-between uppercase tracking-widest text-slate-800">AI Major Rules <Sliders size={20} className="text-indigo-600 cursor-pointer" /></h3>
                <div className="space-y-4">
                  {MAJOR_RULES.map(rule => (
                    <div key={rule.major} className="p-4 border border-slate-100 rounded-2xl space-y-2 hover:border-indigo-600 transition-all duration-500">
                      <div className="flex items-center justify-between">
                        <span className="font-black text-slate-800 text-xs tracking-tight uppercase leading-none">{rule.major}</span>
                        <Badge variant="emerald">Active</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(rule.weights).length === 0 ? (
                          <span className="text-[9px] text-slate-400 uppercase tracking-widest">Interest-led</span>
                        ) : (
                          Object.entries(rule.weights).map(([subject, weight]) => (
                            <span key={subject} className="text-[9px] font-black text-slate-600 uppercase tracking-widest px-2 py-1 bg-slate-50 rounded-lg border border-slate-100">
                              {subject}: {Math.round(weight * 100)}%
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
          {activeTab === 'a-settings' && (
            <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-500 text-left">
              <h1 className="text-3xl font-black text-center text-slate-900 tracking-tighter uppercase leading-none">System Architecture Settings</h1>
              <Card className="space-y-6 p-8 rounded-[40px] border-0 shadow-xl bg-white">
                {[
                  { key: 'aiRecommendations', label: 'AI Major Recommendations', desc: 'Use test performance + interests to suggest majors.' },
                  { key: 'autoSyncTests', label: 'Auto Sync Tests', desc: 'Pull latest assessments from the academic system.' },
                  { key: 'atRiskAlerts', label: 'At-Risk Alerts', desc: 'Notify counselors on declining performance.' },
                  { key: 'counselorDigest', label: 'Weekly Counselor Digest', desc: 'Email weekly summaries of student progress.' },
                ].map(setting => (
                  <div
                    key={setting.key}
                    onClick={() => toggleSetting(setting.key)}
                    className="flex justify-between items-center p-5 bg-slate-50 rounded-2xl border border-slate-100 group cursor-pointer hover:border-indigo-100 transition-all shadow-inner"
                  >
                    <div>
                      <p className="font-black text-slate-800 text-base leading-none">{setting.label}</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1 opacity-60">{setting.desc}</p>
                    </div>
                    <div className={`w-12 h-6 rounded-full flex items-center p-1 transition-all shadow-inner ${systemSettings[setting.key] ? 'bg-emerald-500 justify-end' : 'bg-slate-200 justify-start'}`}>
                      <div className="w-4 h-4 bg-white rounded-full shadow-lg" />
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.3em] shadow-2xl hover:bg-black transition-all">Export System Log</button>
                  <button className="w-full py-4 bg-white border border-slate-200 text-slate-700 rounded-2xl font-black uppercase text-[10px] tracking-[0.3em] shadow-2xl hover:border-indigo-600 transition-all">Reset Alerts</button>
                </div>
              </Card>
            </div>
          )}

          {/* Website Builder Features */}


          {/* Universal Fallback Block */}
          {!['dashboard', 'assessment', 'multimedia', 'paths', 'institutions', 'explore', 'role-materials', 'roadmap', 'locker', 'goals', 'c-dashboard', 'c-students', 'c-notes', 'c-earnings', 'c-availability', 'a-dashboard', 'a-outcomes', 'a-users', 'a-cms', 'a-settings'].includes(activeTab) && (
            <div className="h-full flex flex-col items-center justify-center text-center p-12 animate-in zoom-in-95 duration-500">
              <div className="w-14 h-14 bg-indigo-50 border border-indigo-100 rounded-3xl flex items-center justify-center text-indigo-600 mb-8 shadow-2xl shadow-indigo-100/50 animate-bounce"><MonitorPlay size={24} /></div>
              <h3 className="text-2xl font-black text-slate-800 tracking-tight uppercase leading-none">Module Processing</h3>
              <p className="text-slate-500 max-w-sm mt-3 text-sm font-medium leading-relaxed">Processing 2025 data instances for your operational perspective.</p>
              <button onClick={() => setActiveTab(user.role === 'student' ? 'dashboard' : user.role === 'counselor' ? 'c-dashboard' : 'a-dashboard')} className="mt-10 px-10 py-3.5 bg-slate-900 text-white rounded-xl font-black uppercase text-[9px] tracking-widest shadow-2xl hover:bg-indigo-600 transition-all">Reset Entry Portal</button>
            </div>
          )}


        </main>
      </div >
    </div >
  );
};

export default App;




